import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaCheck, FaTimes } from 'react-icons/fa';

interface Feature {
  name: string;
  included: boolean | 'partial';
  highlight?: boolean;
}

interface PricePlan {
  id: string;
  name: string;
  description: string;
  price: number;
  discountPrice?: number;
  period: string;
  isPopular?: boolean;
  features: Feature[];
  ctaText?: string;
  ctaLink?: string;
  additionalText?: string;
}

interface PricingTableProps {
  title: string;
  subtitle?: string;
  plans: PricePlan[];
  showAnnualToggle?: boolean;
}

const PricingContainer = styled.section`
  padding: 5rem 1rem;
  background-color: #f8f9fa;
`;

const PricingHeader = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 4rem;
`;

const PricingTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  
  @media (min-width: 768px) {
    font-size: 3rem;
  }
`;

const PricingSubtitle = styled.p`
  font-size: 1.2rem;
  color: var(--gray);
  margin-bottom: 2rem;
`;

const ToggleContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 3rem;
  align-items: center;
  gap: 1rem;
`;

const ToggleLabel = styled.span<{ active: boolean }>`
  font-size: 1rem;
  font-weight: ${props => props.active ? '600' : '400'};
  color: ${props => props.active ? 'var(--gray-dark)' : 'var(--gray)'};
`;

const ToggleSwitch = styled.div`
  position: relative;
  width: 60px;
  height: 30px;
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;
  
  &:checked + span {
    background-color: var(--blue);
  }
  
  &:checked + span:before {
    transform: translateX(30px);
  }
`;

const ToggleSlider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  border-radius: 34px;
  transition: .4s;
  
  &:before {
    position: absolute;
    content: "";
    height: 22px;
    width: 22px;
    left: 4px;
    bottom: 4px;
    background-color: white;
    border-radius: 50%;
    transition: .4s;
  }
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  }
`;

const PriceCard = styled.div<{ isPopular?: boolean }>`
  background-color: white;
  border-radius: 1rem;
  box-shadow: ${props => props.isPopular ? '0 15px 30px rgba(0, 123, 255, 0.2)' : '0 5px 15px rgba(0, 0, 0, 0.05)'};
  overflow: hidden;
  position: relative;
  transition: transform 0.3s ease;
  
  ${props => props.isPopular && `
    border: 2px solid var(--blue);
    transform: scale(1.05);
    
    @media (max-width: 767px) {
      transform: scale(1);
    }
  `}
  
  &:hover {
    transform: ${props => props.isPopular ? 'scale(1.08)' : 'scale(1.03)'};
    
    @media (max-width: 767px) {
      transform: ${props => props.isPopular ? 'scale(1.03)' : 'scale(1.03)'};
    }
  }
`;

const PopularBadge = styled.div`
  position: absolute;
  top: 0;
  right: 2rem;
  background-color: var(--blue);
  color: white;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 0.5rem 1rem;
  border-radius: 0 0 0.5rem 0.5rem;
  z-index: 1;
`;

const PriceHeader = styled.div`
  padding: 2rem;
  text-align: center;
  border-bottom: 1px solid #f0f0f0;
`;

const PriceName = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const PriceDescription = styled.p`
  font-size: 0.95rem;
  color: var(--gray);
  margin-bottom: 1.5rem;
  min-height: 2.8rem;
`;

const PriceTag = styled.div`
  margin-bottom: 1rem;
`;

const OriginalPrice = styled.span`
  text-decoration: line-through;
  color: var(--gray);
  font-size: 1.2rem;
  margin-right: 0.5rem;
`;

const CurrentPrice = styled.span`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--gray-dark);
`;

const Currency = styled.span`
  font-size: 1.2rem;
  font-weight: 400;
  vertical-align: super;
  color: var(--gray);
`;

const Period = styled.div`
  font-size: 0.9rem;
  color: var(--gray);
`;

const PriceBody = styled.div`
  padding: 2rem;
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 2rem 0;
`;

const FeatureItem = styled.li<{ included: boolean | 'partial', highlight?: boolean }>`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  color: ${props => props.included === true ? 'inherit' : 'var(--gray)'};
  font-weight: ${props => props.highlight ? '600' : '400'};
  
  svg {
    margin-right: 0.75rem;
    color: ${props => {
      if (props.included === true) return 'var(--green)';
      if (props.included === 'partial') return 'var(--orange)';
      return 'var(--gray)';
    }};
  }
`;

const CtaButton = styled(Link)<{ isPopular?: boolean }>`
  display: block;
  width: 100%;
  text-align: center;
  background-color: ${props => props.isPopular ? 'var(--blue)' : 'transparent'};
  color: ${props => props.isPopular ? 'white' : 'var(--blue)'};
  border: ${props => props.isPopular ? 'none' : '1px solid var(--blue)'};
  border-radius: 0.5rem;
  padding: 1rem;
  font-size: 1rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.isPopular ? 'var(--indigo)' : 'rgba(0, 123, 255, 0.1)'};
    color: ${props => props.isPopular ? 'white' : 'var(--blue)'};
    text-decoration: none;
  }
`;

const AdditionalText = styled.p`
  font-size: 0.85rem;
  color: var(--gray);
  text-align: center;
  margin-top: 1rem;
`;

const PricingTable: React.FC<PricingTableProps> = ({
  title,
  subtitle,
  plans,
  showAnnualToggle = true
}) => {
  const [isAnnual, setIsAnnual] = useState<boolean>(true);
  
  // Función para calcular el precio según el período seleccionado
  const calculatePrice = (plan: PricePlan) => {
    if (isAnnual) {
      return plan.discountPrice || plan.price;
    }
    // Para precios mensuales, multiplicamos por 1.5 (simulando que el anual tiene 30% de descuento)
    return plan.price * 1.5;
  };

  return (
    <PricingContainer>
      <PricingHeader>
        <PricingTitle>{title}</PricingTitle>
        {subtitle && <PricingSubtitle>{subtitle}</PricingSubtitle>}
        
        {showAnnualToggle && (
          <ToggleContainer>
            <ToggleLabel active={!isAnnual}>Mensual</ToggleLabel>
            <ToggleSwitch>
              <ToggleInput 
                type="checkbox" 
                checked={isAnnual}
                onChange={() => setIsAnnual(!isAnnual)}
              />
              <ToggleSlider />
            </ToggleSwitch>
            <ToggleLabel active={isAnnual}>Anual <strong>(30% descuento)</strong></ToggleLabel>
          </ToggleContainer>
        )}
      </PricingHeader>
      
      <PricingGrid>
        {plans.map(plan => (
          <PriceCard key={plan.id} isPopular={plan.isPopular}>
            {plan.isPopular && <PopularBadge>MÁS POPULAR</PopularBadge>}
            <PriceHeader>
              <PriceName>{plan.name}</PriceName>
              <PriceDescription>{plan.description}</PriceDescription>
              <PriceTag>
                {plan.discountPrice && isAnnual && (
                  <OriginalPrice>{plan.price}€</OriginalPrice>
                )}
                <CurrentPrice>
                  {calculatePrice(plan).toFixed(0)}€
                </CurrentPrice>
              </PriceTag>
              <Period>/{plan.period}</Period>
            </PriceHeader>
            <PriceBody>
              <FeaturesList>
                {plan.features.map((feature, index) => (
                  <FeatureItem 
                    key={index} 
                    included={feature.included} 
                    highlight={feature.highlight}
                  >
                    {feature.included === true && <FaCheck />}
                    {feature.included === 'partial' && <FaCheck />}
                    {feature.included === false && <FaTimes />}
                    {feature.name}
                  </FeatureItem>
                ))}
              </FeaturesList>
              <CtaButton 
                to={plan.ctaLink || '/contacto'} 
                isPopular={plan.isPopular}
              >
                {plan.ctaText || 'Elegir plan'}
              </CtaButton>
              {plan.additionalText && (
                <AdditionalText>{plan.additionalText}</AdditionalText>
              )}
            </PriceBody>
          </PriceCard>
        ))}
      </PricingGrid>
    </PricingContainer>
  );
};

export default PricingTable; 